import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { LANG_LIST } from "@/lib/languages";

const FLAG_FONT = { fontFamily: '"Twemoji Country Flags", "Apple Color Emoji", "Segoe UI Emoji", sans-serif' };
const flagOf = (id) => LANG_LIST.find((l) => l.id === id)?.flag;

const DEMOS = [
  { id: "hi", lang: "Hindi", said: "अरे यार, उम, कल की मीटिंग तीन बजे रख दो ना", out: "Let's schedule tomorrow's meeting for 3 PM." },
  { id: "es", lang: "Spanish", said: "eh, hola Marco, este… ¿puedes mandarme el informe hoy?", out: "Hi Marco, could you send me the report today?" },
  { id: "fr", lang: "French", said: "euh bonjour, alors, je serai en retard de, euh, dix minutes", out: "Hello, I'll be about ten minutes late." },
  { id: "ja", lang: "Japanese", said: "えーと、明日の会議は、あの、延期になりました", out: "Tomorrow's meeting has been postponed." },
  { id: "hinglish", lang: "Hinglish", said: "bhai, um, client ko proposal aaj hi bhej dena please", out: "Please send the proposal to the client today." },
  { id: "ta", lang: "Tamil", said: "ம்ம், நாளைக்கு நான் வீட்டிலிருந்து வேலை செய்வேன்", out: "I'll be working from home tomorrow." },
  { id: "de", lang: "German", said: "ähm, kannst du, äh, die Rechnung bis Freitag schicken?", out: "Could you send the invoice by Friday?" },
];

function Marquee() {
  const row = LANG_LIST.slice(0, 60);
  return (
    <div className="relative overflow-hidden mt-10 [mask-image:linear-gradient(90deg,transparent,black_10%,black_90%,transparent)]" data-testid="languages-marquee">
      <div className="flex gap-2 w-max animate-[marquee_60s_linear_infinite]">
        {[...row, ...row].map((l, i) => (
          <span key={i} className="pill text-xs whitespace-nowrap !cursor-default"><span style={FLAG_FONT}>{l.flag}</span> {l.label}</span>
        ))}
      </div>
    </div>
  );
}

export function LanguagesShowcase() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setI((n) => (n + 1) % DEMOS.length), 3200);
    return () => clearTimeout(t);
  }, [i]);
  const d = DEMOS[i];
  return (
    <section className="px-6 sm:px-12 pb-24 max-w-6xl" data-testid="landing-languages">
      <div className="eyebrow mb-4">100+ languages</div>
      <h2 className="font-serif text-4xl sm:text-5xl text-[var(--ink)] leading-tight max-w-3xl">
        Speak in <em className="text-[var(--accent)]">any language.</em> Get it written in the one you need.
      </h2>
      <p className="mt-4 text-sm sm:text-base text-white/50 max-w-xl">Tick the languages you speak, mix them mid-sentence, or translate while you talk. Mulankan cleans it up either way.</p>
      <div className="panel mt-10 p-6 sm:p-10 grid lg:grid-cols-[1fr_auto_1fr] gap-6 lg:gap-10 items-center">
        <div key={`s-${i}`} className="fade-up min-h-[96px]" data-testid="languages-demo-said">
          <div className="eyebrow mb-3 flex items-center gap-2"><span style={FLAG_FONT}>{flagOf(d.id)}</span> You say · {d.lang}</div>
          <p className="text-white/55 italic text-lg">"{d.said}"</p>
        </div>
        <ArrowRight size={22} className="text-[var(--accent)] rotate-90 lg:rotate-0 justify-self-center" />
        <div key={`o-${i}`} className="fade-up min-h-[96px]" style={{ animationDelay: "350ms" }} data-testid="languages-demo-out">
          <div className="eyebrow mb-3 flex items-center gap-2"><span style={FLAG_FONT}>{flagOf("en")}</span> Mulankan writes · English</div>
          <p className="text-[var(--ink)] text-lg leading-relaxed">{d.out}</p>
        </div>
      </div>
      <div className="flex gap-1.5 mt-5" data-testid="languages-demo-dots">
        {DEMOS.map((x, n) => (
          <button key={x.id} onClick={() => setI(n)} aria-label={`Show ${x.lang} example`} data-testid={`languages-demo-dot-${x.id}`}
            className={`h-1.5 rounded-full transition-[width,background-color] duration-300 ${n === i ? "w-6 bg-[var(--accent)]" : "w-1.5 bg-white/20"}`} />
        ))}
      </div>
      <Marquee />
    </section>
  );
}
